/* Bundle Builder — buy-box block logic (renders via snippets/bundle-builder.liquid).
   Single POST to /cart/add.js; duplicate variants merge into one line with quantity.
   1-slot tiers add with no properties at all (identical to the normal ATC line).
   Multi-slot tiers carry visible "Bundle" + hidden _bundle_id/_bundle_tier/
   _bundle_discount_pct diagnostics. Discounts come from automatic discount rules
   in admin — nothing here computes a charge. */
if (!customElements.get('bundle-builder')) {
  console.info('[bundle-builder] v3.1 loaded (amount-based mix pricing)');
  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      connectedCallback() {
        const dataEl = this.querySelector('[data-bundle-data]');
        try {
          this.data = JSON.parse(dataEl.textContent);
        } catch (e) {
          console.error('[bundle-builder] Product data JSON is malformed; hiding the module.', e);
          this.hidden = true;
          return;
        }
        this.productsByHandle = {};
        this.data.products.forEach((p) => (this.productsByHandle[p.handle] = p));

        this.tiers = Array.from(this.querySelectorAll('[data-tier]')).map((el) => ({
          el: el,
          key: el.dataset.tierKey,
          slotCount: parseInt(el.dataset.slotCount, 10),
          pct: parseInt(el.dataset.discountPct, 10),
          priceCents: parseInt(el.dataset.priceCents, 10) || 0,
          fullCents: parseInt(el.dataset.fullCents, 10) || 0,
          offEachCents: parseInt(el.dataset.offEachCents, 10) || 0,
          radio: el.querySelector('input[type="radio"]'),
          slotsEl: el.querySelector('[data-slots]'),
          label: el.querySelector('.tt-bundle-tier-label').textContent
        }));
        this.defaultTier = parseInt(this.dataset.defaultTier, 10) || 0;
        this.liveRegion = this.querySelector('[data-live-region]');
        this.atcBtn = this.querySelector('[data-atc]');
        this.atcReason = this.querySelector('[data-atc-reason]');
        this.errorEl = this.querySelector('[data-error]');
        this.openPickerSlot = null;
        this.pending = false;
        this.failedHandles = new Set();

        /* Popular add-ons: ordinary extras the customer ticks. They ride along
           with the bundle in the same cart post but carry no bundle properties
           — they are not part of the pack. */
        this.addons = Array.from(this.querySelectorAll('[data-addon]')).map((el) => ({
          el: el,
          check: el.querySelector('[data-addon-check]'),
          variantEl: el.querySelector('[data-addon-variant]'),
          priceEl: el.querySelector('[data-addon-price]'),
          compareEl: el.querySelector('[data-addon-compare]'),
          title: (el.querySelector('.tt-bundle-addon-title') || {}).textContent || '',
          /* cable cards: the family dropdown swaps the whole card to a
             sibling product, so the title, image and link move with it */
          isCable: el.hasAttribute('data-addon-cable'),
          familyEl: el.querySelector('[data-addon-family]'),
          titleEl: el.querySelector('[data-addon-title]'),
          linkEl: el.querySelector('[data-addon-link]'),
          imgEl: el.querySelector('[data-addon-img]')
        }));

        this.tiers.forEach((tier, i) => {
          tier.radio.addEventListener('change', () => {
            this.selectTier(i);
            this.announce(tier.label);
          });
        });
        this.addons.forEach((addon) => {
          if (addon.check) addon.check.addEventListener('change', () => this.renderAtc());
          const row = addon.el.querySelector('[data-addon-row]');
          if (row) {
            row.addEventListener('click', (e) => {
              /* whole row ticks the box, except the controls inside it */
              if (e.target.closest('a, select, input, button')) return;
              if (!addon.check || addon.check.disabled) return;
              addon.check.checked = !addon.check.checked;
              this.renderAtc();
            });
          }
          if (addon.variantEl && addon.variantEl.tagName === 'SELECT') {
            addon.variantEl.addEventListener('change', () => {
              this.renderAddonPrice(addon);
              this.renderAtc();
            });
          }
          if (addon.familyEl) {
            addon.familyEl.addEventListener('change', () => this.swapAddonFamily(addon));
            /* the select sits inside the row but must not tick the box */
            addon.familyEl.addEventListener('click', (e) => e.stopPropagation());
          }
        });
        /* Smart add-on pool (AOV targeting): pool candidates render hidden and
           the two shown are chosen to push cart + current selection to the
           target. With no pool in the markup this whole feature is inert. */
        const addonsWrap = this.querySelector('[data-addons]');
        this.addonsWrap = addonsWrap;
        this.aovTarget = addonsWrap ? parseInt(addonsWrap.dataset.aovTarget, 10) || 0 : 0;
        this.poolAddons = this.addons.filter((a) => a.el.hasAttribute('data-addon-pool'));
        this.cartCents = 0;
        if (this.poolAddons.length && this.aovTarget > 0) this.refreshCartTotal();

        /* Fleet mode: the fleet card is an ordinary tier whose numbers the
           cases dropdown rewrites — slot count, price, and full price all
           scale linearly with the case count, so every existing path
           (selection, totals, URL, add-to-cart) works unchanged. */
        this.fleetSelect = this.querySelector('[data-fleet-cases]');
        if (this.fleetSelect) {
          this.fleetSelect.addEventListener('change', () => this.updateFleet());
          /* the select sits inside the card but must not toggle the radio */
          this.fleetSelect.addEventListener('click', (e) => e.stopPropagation());
        }

        this.atcBtn.addEventListener('click', () => this.addToCart());
        this.onPopState = () => {
          this.state = this.readUrl();
          this.render();
        };
        window.addEventListener('popstate', this.onPopState);
        this.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.openPickerSlot !== null) this.closePicker(true);
        });

        this.state = this.readUrl();
        this.render();
        this.hideStandardAtc();
        try {
          this.setupFloatingAtc();
        } catch (e) {
          console.error('[bundle-builder] floating setup failed:', e);
        }
      }

      disconnectedCallback() {
        window.removeEventListener('popstate', this.onPopState);
        if (this.ioModule) this.ioModule.disconnect();
        if (this.onFloatCheck) {
          window.removeEventListener('scroll', this.onFloatCheck);
          window.removeEventListener('resize', this.onFloatCheck);
        }
        /* if the button (or fade) is parked on <body> when this section
           re-renders, remove them so they can't linger as orphans */
        if (this.floating && this.atcBtn) this.atcBtn.remove();
        if (this.fadeEl) this.fadeEl.remove();
      }

      /* ---------- floating buy button ----------
         Keeps the button reachable while a tall tier is open. Driven by
         IntersectionObserver (fires regardless of how the page scrolls,
         including inside the theme editor's preview iframe). The button is
         re-parented to <body> with position:fixed because the theme's
         scroll-in animations leave transforms on the product column that
         break fixed/sticky positioning inside it. */

      setupFloatingAtc() {
        this.floating = false;
        /* While pinned, the bar belongs to the product section: it drops away
           at the end of it rather than riding over the sections below.
           Falls back to the module itself if the markup ever changes. */
        this.floatScope = this.closest('.shopify-section') || this.closest('.product') || this;
        this.placeholder = document.createElement('div');
        this.placeholder.className = 'tt-bundle-atc-placeholder';
        this.atcBtn.insertAdjacentElement('afterend', this.placeholder);
        this.fadeEl = document.createElement('div');
        this.fadeEl.className = 'tt-bundle-atc-fade';
        this.fadeEl.setAttribute('aria-hidden', 'true');
        document.body.appendChild(this.fadeEl);

        /* Two independent triggers — observers and scroll events — so that if
           either is broken or sandboxed on the page, the other still drives
           the float. Both funnel into the same rect-based updateFloat. */
        this.onFloatCheck = () => {
          if (this.floatRaf) return;
          this.floatRaf = requestAnimationFrame(() => {
            this.floatRaf = null;
            this.updateFloat();
            this.positionFloat();
          });
        };
        window.addEventListener('scroll', this.onFloatCheck, { passive: true });
        window.addEventListener('resize', this.onFloatCheck);

        if ('IntersectionObserver' in window) {
          this.ioModule = new IntersectionObserver(this.onFloatCheck, {
            rootMargin: '0px 0px -10px 0px',
            threshold: [0, 0.1, 0.5, 1]
          });
          this.ioModule.observe(this);
          if (this.floatScope && this.floatScope !== this) this.ioModule.observe(this.floatScope);
        }

        this.updateFloat();
        console.info('[bundle-builder] floating armed');
      }

      updateFloat() {
        /* The button belongs to the bundle box and sits attached under the
           tiers. It only lifts to a pinned bottom bar while that resting spot
           is off screen — above it or below it — so it is always reachable
           without ever detaching while you can see where it lives.
           The tier cards are a fixed height now, so switching packs cannot
           move the resting spot: no stepping. */
        if (!this.placeholder) return;
        const h = this.atcBtn.offsetHeight || 52;
        const anchor = (this.floating ? this.placeholder : this.atcBtn).getBoundingClientRect();
        const bottom = anchor.top + h;
        /* wider band to release than to pin, so a scroll that parks right on
           the threshold can't flutter between the two states */
        const wantFloat = this.floating
          ? bottom > window.innerHeight - 40 || anchor.top < 24
          : bottom > window.innerHeight - 12 || anchor.top < 0;
        if (wantFloat !== this.floating) this.setFloating(wantFloat);
        if (this.floating) {
          this.positionFloat();
          this.updateFloatRange();
        }
      }

      setFloating(on) {
        this.floating = on;
        console.info('[bundle-builder] floating ' + (on ? 'engaged' : 'released'));
        if (on) {
          /* reserve the button's own space so nothing below it jumps up */
          const gap = parseFloat(getComputedStyle(this.atcBtn).marginTop) || 0;
          this.placeholder.style.height = this.atcBtn.offsetHeight + gap + 'px';
          this.placeholder.classList.add('is-active');
          this.atcBtn.classList.add('tt-bundle-atc-floating');
          document.body.appendChild(this.atcBtn);
          if (this.fadeEl) this.fadeEl.classList.add('is-active');
        } else {
          this.placeholder.parentNode.insertBefore(this.atcBtn, this.placeholder);
          this.atcBtn.classList.remove('tt-bundle-atc-floating', 'is-dismissed');
          this.atcBtn.style.left = '';
          this.atcBtn.style.width = '';
          this.placeholder.classList.remove('is-active');
          this.placeholder.style.height = '';
          this.floatHidden = false;
          if (this.fadeEl) this.fadeEl.classList.remove('is-active', 'is-dismissed');
        }
      }

      /* Show the bar only while the product section is on screen. It drops out
         once the section's end passes above the bar, and comes back on the way
         up — a fade, not a position change, so nothing ever steps. */
      updateFloatRange() {
        const scope = this.floatScope || this;
        const r = scope.getBoundingClientRect();
        const barTop = window.innerHeight - this.atcBtn.offsetHeight - 24;
        const sectionDone = r.bottom <= barTop;
        const sectionNotReached = r.top > window.innerHeight;
        const hide = sectionDone || sectionNotReached;
        if (hide === this.floatHidden) return;
        this.floatHidden = hide;
        this.atcBtn.classList.toggle('is-dismissed', hide);
        if (this.fadeEl) this.fadeEl.classList.toggle('is-dismissed', hide);
      }

      positionFloat() {
        if (!this.floating) return;
        const r = this.getBoundingClientRect();
        this.atcBtn.style.left = r.left + 'px';
        this.atcBtn.style.width = r.width + 'px';
        if (this.fadeEl) {
          this.fadeEl.style.left = r.left - 16 + 'px';
          this.fadeEl.style.width = r.width + 32 + 'px';
        }
      }

      /* Replace the theme's own Add to cart on this product (progressive:
         if this script never runs, the standard button stays usable). */
      hideStandardAtc() {
        if (!this.data.hideStandardAtc) return;
        const scope = this.closest('.product__info-wrapper');
        if (!scope) return;
        scope.querySelectorAll('.product-form-wrapper').forEach((el) => {
          el.style.display = 'none';
        });
      }

      /* ---------- state ---------- */

      defaultState() {
        /* a fleet tier always self-fills with the viewed product — a case is
           a quantity of one product even when mix & match is on elsewhere */
        const isFleet = this.tiers[this.defaultTier].el.hasAttribute('data-fleet');
        const fill = this.data.prefill || isFleet ? this.data.currentHandle : null;
        const slots = new Array(this.tiers[this.defaultTier].slotCount).fill(fill);
        slots[0] = this.data.currentHandle;
        return { tierIndex: this.defaultTier, slots: slots };
      }

      selectTier(index) {
        const prev = this.state.slots;
        const count = this.tiers[index].slotCount;
        const isFleet = this.tiers[index].el.hasAttribute('data-fleet');
        const fill = this.data.prefill || isFleet ? this.data.currentHandle : null;
        const slots = new Array(count).fill(fill);
        for (let i = 0; i < Math.min(count, prev.length); i++) {
          if (prev[i]) slots[i] = prev[i];
        }
        if (!slots[0]) slots[0] = this.data.currentHandle;
        this.state = { tierIndex: index, slots: slots };
        this.clearError();
        this.closePicker(false);
        this.render();
      }

      updateFleet() {
        const idx = this.tiers.findIndex((t) => t.el.hasAttribute('data-fleet'));
        if (idx === -1) return;
        const tier = this.tiers[idx];
        const cases = parseInt(this.fleetSelect.value, 10) || 1;
        const caseSize = parseInt(tier.el.dataset.fleetCaseSize, 10) || 1;
        const casePrice = parseInt(tier.el.dataset.fleetCasePrice, 10) || 0;
        const caseFull = parseInt(tier.el.dataset.fleetCaseFull, 10) || 0;
        tier.slotCount = caseSize * cases;
        tier.priceCents = casePrice * cases;
        tier.fullCents = caseFull * cases;
        tier.el.dataset.slotCount = tier.slotCount;
        tier.el.dataset.priceCents = tier.priceCents;
        tier.el.dataset.fullCents = tier.fullCents;
        const priceEl = tier.el.querySelector('[data-fleet-price]');
        if (priceEl) priceEl.textContent = this.formatMoney(tier.priceCents);
        const wasEl = tier.el.querySelector('[data-fleet-was]');
        if (wasEl) wasEl.textContent = this.formatMoney(tier.fullCents);
        /* selecting the tier (or reselecting it) resizes state.slots to the
           new count; per-unit price and save % are case-count invariant */
        this.selectTier(idx);
        this.announce(tier.label + ': ' + cases + (cases === 1 ? ' case' : ' cases'));
      }

      setSlot(slotIndex, handle) {
        this.state.slots[slotIndex] = handle;
        this.clearError();
        this.closePicker(false);
        this.render();
        const p = this.productsByHandle[handle];
        this.announce((slotIndex + 1) + ': ' + (p ? p.title : handle));
        const btn = this.tiers[this.state.tierIndex].slotsEl.querySelector('[data-slot-index="' + slotIndex + '"]');
        if (btn) btn.focus();
      }

      isComplete() {
        return this.state.slots.every((h) => !!h);
      }

      /* ---------- URL contract — URL is untrusted input ---------- */

      readUrl() {
        try {
          const params = new URLSearchParams(window.location.search);
          const active = [];
          const handles = {};
          for (const [key, value] of params.entries()) {
            let m = key.match(/^bundle\[(\d+)\]\[active\]$/);
            if (m && value === 'true') active.push(parseInt(m[1], 10));
            m = key.match(/^bundle\[(\d+)\]\[active_handles\]\[(\d+)\]$/);
            if (m) {
              const t = parseInt(m[1], 10);
              (handles[t] = handles[t] || {})[parseInt(m[2], 10)] = value;
            }
          }
          if (active.length !== 1) return this.defaultState();
          const tierIndex = active[0];
          if (tierIndex < 0 || tierIndex >= this.tiers.length) return this.defaultState();
          const count = this.tiers[tierIndex].slotCount;
          const tierHandles = handles[tierIndex] || {};
          const slots = [];
          for (let i = 0; i < count; i++) {
            const h = tierHandles[i];
            const p = h ? this.productsByHandle[h] : null;
            if (!p || !p.available) return this.defaultState();
            slots.push(h);
          }
          if (Object.keys(tierHandles).length !== count) return this.defaultState();
          return { tierIndex: tierIndex, slots: slots };
        } catch (e) {
          return this.defaultState();
        }
      }

      writeUrl() {
        try {
          const url = new URL(window.location.href);
          const keep = [];
          for (const key of url.searchParams.keys()) {
            if (/^bundle\[/.test(key)) keep.push(key);
          }
          keep.forEach((k) => url.searchParams.delete(k));
          if (this.isComplete()) {
            url.searchParams.set('bundle[' + this.state.tierIndex + '][active]', 'true');
            this.state.slots.forEach((h, i) => {
              url.searchParams.set('bundle[' + this.state.tierIndex + '][active_handles][' + i + ']', h);
            });
          }
          history.replaceState(history.state, '', url.toString());
        } catch (e) {
          /* URL write is never worth breaking the page over */
        }
      }

      /* ---------- rendering ---------- */

      render() {
        const focused = document.activeElement && document.activeElement.dataset
          ? document.activeElement.dataset.slotIndex
          : null;

        this.tiers.forEach((tier, i) => {
          const selected = i === this.state.tierIndex;
          tier.el.classList.toggle('is-selected', selected);
          tier.radio.checked = selected;
          tier.slotsEl.hidden = !selected;
          tier.slotsEl.innerHTML = '';
          if (selected) this.renderSlots(tier);
        });
        this.renderSummary();
        this.renderAtc();
        this.chooseAddons();
        this.writeUrl();
        if (this.placeholder) this.updateFloat();

        if (focused !== null && focused !== undefined) {
          const again = this.tiers[this.state.tierIndex].slotsEl.querySelector('[data-slot-index="' + focused + '"]');
          if (again) again.focus();
        }
      }

      renderSlots(tier) {
        /* The fleet card never shows slots — a case of 12 as a thumbnail
           strip is noise; the dropdown already says what you get. */
        if (tier.el.hasAttribute('data-fleet')) return;
        /* With pre-fill on, bundles are quantity packs of the viewed product:
           slots are display-only, no picker (mix & match deliberately off).
           Turning the pre-fill setting off restores the picker. */
        const locked = !!this.data.prefill;
        tier.slotsEl.classList.toggle('is-thumbs', locked);
        if (locked) return this.renderThumbs(tier);
        this.state.slots.forEach((handle, i) => {
          const p = handle ? this.productsByHandle[handle] : null;
          const wrap = document.createElement('div');
          wrap.className = 'tt-bundle-slot';
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className =
            'tt-bundle-slot-btn' +
            (p ? ' is-filled' : '') +
            (handle && this.failedHandles.has(handle) ? ' has-error' : '');
          btn.dataset.slotIndex = i;
          if (locked) {
            btn.disabled = true;
          } else {
            btn.setAttribute('aria-haspopup', 'listbox');
            btn.setAttribute('aria-expanded', 'false');
          }

          const idx = document.createElement('span');
          idx.className = 'tt-bundle-slot-index';
          idx.textContent = i + 1;
          btn.appendChild(idx);

          if (p) {
            if (p.image) {
              const img = document.createElement('img');
              img.src = p.image;
              img.alt = '';
              img.width = 44;
              img.height = 44;
              btn.appendChild(img);
            }
            const title = document.createElement('span');
            title.className = 'tt-bundle-slot-title';
            title.textContent = p.title;
            btn.appendChild(title);
            const price = document.createElement('span');
            price.className = 'tt-bundle-slot-price';
            price.textContent = this.formatMoney(p.priceCents);
            btn.appendChild(price);
          } else {
            const empty = document.createElement('span');
            empty.className = 'tt-bundle-slot-empty';
            empty.textContent = this.data.strings.emptyLabel;
            btn.appendChild(empty);
          }

          if (!locked) btn.addEventListener('click', () => this.togglePicker(i));
          wrap.appendChild(btn);
          tier.slotsEl.appendChild(wrap);
        });
        if (!locked && this.openPickerSlot !== null) this.renderPicker();
      }

      /* Pre-filled packs: show what's in the pack as a strip of thumbnails —
         you see the device (and how many), without the title and price of each
         one repeating what the page already says. One row per pack size, so
         the card height never changes between tiers. */
      renderThumbs(tier) {
        const strip = document.createElement('div');
        strip.className = 'tt-bundle-thumbs';
        this.state.slots.forEach((handle) => {
          const p = handle ? this.productsByHandle[handle] : null;
          if (!p) return;
          const cell = document.createElement('span');
          cell.className = 'tt-bundle-thumb';
          if (p.image) {
            const img = document.createElement('img');
            img.src = p.image;
            img.alt = '';
            img.width = 48;
            img.height = 48;
            img.loading = 'lazy';
            cell.appendChild(img);
          }
          strip.appendChild(cell);
        });
        tier.slotsEl.appendChild(strip);
      }

      togglePicker(slotIndex) {
        this.openPickerSlot = this.openPickerSlot === slotIndex ? null : slotIndex;
        this.render();
        if (this.openPickerSlot !== null) {
          const first = this.tiers[this.state.tierIndex].slotsEl.querySelector('.tt-bundle-option:not(:disabled)');
          if (first) first.focus();
        }
      }

      closePicker(refocus) {
        if (this.openPickerSlot === null) return;
        const idx = this.openPickerSlot;
        this.openPickerSlot = null;
        this.render();
        if (refocus) {
          const btn = this.tiers[this.state.tierIndex].slotsEl.querySelector('[data-slot-index="' + idx + '"]');
          if (btn) btn.focus();
        }
      }

      renderPicker() {
        const slotIndex = this.openPickerSlot;
        const tier = this.tiers[this.state.tierIndex];
        const slotWrap = tier.slotsEl.children[slotIndex];
        if (!slotWrap) {
          this.openPickerSlot = null;
          return;
        }
        slotWrap.querySelector('.tt-bundle-slot-btn').setAttribute('aria-expanded', 'true');

        const list = document.createElement('div');
        list.className = 'tt-bundle-picker';
        list.setAttribute('role', 'listbox');
        list.setAttribute('aria-label', this.data.strings.pickerLabel);

        this.data.products.forEach((p) => {
          const opt = document.createElement('button');
          opt.type = 'button';
          opt.className = 'tt-bundle-option';
          opt.setAttribute('role', 'option');
          opt.setAttribute('aria-selected', this.state.slots[slotIndex] === p.handle ? 'true' : 'false');
          if (!p.available) opt.disabled = true;

          if (p.image) {
            const img = document.createElement('img');
            img.src = p.image;
            img.alt = '';
            img.width = 32;
            img.height = 32;
            opt.appendChild(img);
          }
          const title = document.createElement('span');
          title.textContent = p.title;
          opt.appendChild(title);
          const right = document.createElement('span');
          if (p.available) {
            right.className = 'tt-bundle-option-price';
            right.textContent = this.formatMoney(p.priceCents);
          } else {
            right.className = 'tt-bundle-option-soldout';
            right.textContent = this.data.strings.soldOut;
          }
          opt.appendChild(right);

          if (p.available) opt.addEventListener('click', () => this.setSlot(slotIndex, p.handle));
          list.appendChild(opt);
        });

        list.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
          e.preventDefault();
          const opts = Array.from(list.querySelectorAll('.tt-bundle-option:not(:disabled)'));
          const at = opts.indexOf(document.activeElement);
          const next = e.key === 'ArrowDown' ? Math.min(at + 1, opts.length - 1) : Math.max(at - 1, 0);
          if (opts[next]) opts[next].focus();
        });

        slotWrap.appendChild(list);
      }

      /* ---------- add-ons ---------- */

      addonVariant(addon) {
        const el = addon.variantEl;
        if (!el) return null;
        const opt = el.tagName === 'SELECT' ? el.options[el.selectedIndex] : el;
        if (!opt) return null;
        const id = parseInt(el.tagName === 'SELECT' ? opt.value : el.value, 10);
        const priceCents = parseInt(opt.dataset.price, 10);
        if (!id || isNaN(priceCents)) return null;
        const pct = parseInt(addon.check && addon.check.dataset.addonPct, 10) || 0;
        return { id: id, priceCents: priceCents, pct: pct };
      }

      checkedAddons() {
        return this.addons
          .filter((a) => a.check && a.check.checked && !a.check.disabled)
          .map((a) => this.addonVariant(a))
          .filter(Boolean);
      }

      renderAddonPrice(addon) {
        const v = this.addonVariant(addon);
        if (!v) return;
        if (addon.priceEl) {
          addon.priceEl.textContent = this.formatMoney(Math.round((v.priceCents * (100 - v.pct)) / 100));
        }
        if (addon.compareEl) addon.compareEl.textContent = this.formatMoney(v.priceCents);
      }

      /* A cable card's dropdown switches to a sibling PRODUCT, not a variant:
         price, title, image and link all move together. Everything downstream
         (totals, add to cart) reads the hidden variant field, so rewriting
         that field is what makes the swap real — the rest is display.
         The card keeps its slot: switching length must not reshuffle the row
         out from under the customer, same rule as ticking a box. */
      swapAddonFamily(addon) {
        const sel = addon.familyEl;
        if (!sel) return;
        const opt = sel.options[sel.selectedIndex];
        if (!opt || !addon.variantEl) return;
        const priceCents = parseInt(opt.dataset.price, 10);
        if (!opt.value || isNaN(priceCents)) return;

        addon.variantEl.value = opt.value;
        addon.variantEl.dataset.price = priceCents;
        /* the card shows the trimmed name, the label keeps the full one */
        const shown = opt.dataset.short || opt.dataset.title;
        if (addon.titleEl && shown) addon.titleEl.textContent = shown;
        if (addon.linkEl && opt.dataset.url) addon.linkEl.href = opt.dataset.url;
        if (addon.imgEl && opt.dataset.image) addon.imgEl.src = opt.dataset.image;
        if (addon.check && opt.dataset.title) {
          addon.check.setAttribute('aria-label', 'Add ' + opt.dataset.title);
        }
        addon.title = opt.dataset.title || addon.title;

        this.renderAddonPrice(addon);
        this.renderAtc();
      }

      refreshCartTotal() {
        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((cart) => {
            this.cartCents = cart.total_price || 0;
            this.chooseAddons();
          })
          .catch(() => this.chooseAddons());
      }

      /* Pick which pool add-ons to show, in a fixed running order:
           slot 1  the priciest non-cable candidate — the biggest single
                   step toward the AOV target, so the push leads the row;
           slot 2  a cable, always, whichever one best covers what is left
                   of the gap after slot 1.
         A ticked add-on is pinned and never swapped out from under the
         customer, and ticking one does not reshuffle the other slot
         (renderAtc, not render). Until the pair:cable tags are applied
         nothing is a cable candidate and this falls back to the original
         best-fit-either-way behaviour, so a half-tagged catalogue still
         fills both slots. */
      chooseAddons() {
        if (!this.poolAddons || !this.poolAddons.length || !this.aovTarget) return;
        const t = this.totals();
        const order = t ? t.discounted : this.tiers[this.state.tierIndex].priceCents;
        const gap = this.aovTarget - this.cartCents - order;

        const pinned = [];
        const cables = [];
        const others = [];
        this.poolAddons.forEach((a) => {
          if (a.check && a.check.checked) return pinned.push(a);
          const v = this.addonVariant(a);
          if (!v) return;
          (a.isCable ? cables : others).push({ a: a, cents: v.priceCents });
        });

        const pinnedCable = pinned.some((a) => a.isCable);
        const pinnedOther = pinned.some((a) => !a.isCable);
        const slots = Math.max(0, 2 - pinned.length);
        let valuePick = null;
        let cablePick = null;

        if (slots > 0) {
          if (!pinnedOther && others.length) {
            others.sort((x, y) => y.cents - x.cents);
            valuePick = others[0];
          }
          /* the cable owns the second slot; with only one slot free and the
             value pick already in it, a pinned cable is what fills the row */
          const cableRoom = valuePick ? slots > 1 : slots > 0;
          if (!pinnedCable && cableRoom && cables.length) {
            const left = gap - (valuePick ? valuePick.cents : 0);
            cables.sort((x, y) => x.cents - y.cents);
            const clears = cables.filter((x) => x.cents >= left);
            cablePick = clears.length ? clears[0] : cables[cables.length - 1];
          }
          /* no cable tagged yet, or one category is empty: top the row back
             up with whatever is left rather than showing a short row */
          const filled = (valuePick ? 1 : 0) + (cablePick ? 1 : 0);
          if (filled < slots) {
            others
              .concat(cables)
              .filter((x) => x !== valuePick && x !== cablePick)
              .sort((x, y) => x.cents - y.cents)
              .slice(0, slots - filled)
              .forEach((x) => {
                if (!valuePick) valuePick = x;
                else if (!cablePick) cablePick = x;
              });
          }
        }

        const chosen = [valuePick, cablePick].filter(Boolean).map((x) => x.a);
        const show = pinned.concat(chosen);
        this.poolAddons.forEach((a) => {
          a.el.hidden = show.indexOf(a) === -1;
        });
        /* Collection order decides which products are candidates, not where
           they land: re-seat the visible cards so the value pick is always
           above the cable. The cable sinks on being a cable, NOT on being
           unpinned — sorting pinned cards to the front instead would jump a
           ticked cable to the top of the row, and it stays ticked across a
           back-navigation, so the row would come back reordered.
           Moving a node keeps its listeners and state, but it is still a
           detach, so only do it when the order actually changed —
           chooseAddons runs on every tier change and cart read. */
        if (this.addonsWrap) {
          const ordered = show.filter((a) => !a.isCable).concat(show.filter((a) => a.isCable));
          const order = ordered.map((a) => this.poolAddons.indexOf(a)).join(',');
          if (order !== this.addonOrderKey) {
            ordered.forEach((a) => this.addonsWrap.appendChild(a.el));
            this.addonOrderKey = order;
          }
        }
      }

      totals() {
        if (!this.isComplete()) return null;
        const tier = this.tiers[this.state.tierIndex];
        const slotsFull = this.state.slots.reduce((sum, h) => sum + this.productsByHandle[h].priceCents, 0);
        let full = slotsFull;
        /* The charm price is precomputed server-side for the prefilled
           same-product bundle. Only trust it when the chosen slots add up to
           the total it was computed against. A mix-and-match cart uses the
           rule's actual mechanics — a fixed amount off EACH item — so a
           $69 slot gets the same dollars off as a $59 one, matching what
           checkout charges. Percentage math is the last resort, for tiers
           whose saving doesn't split evenly per item. */
        let discounted;
        if (tier.priceCents > 0 && slotsFull === tier.fullCents) {
          discounted = tier.priceCents;
        } else if (tier.offEachCents > 0) {
          discounted = Math.max(0, slotsFull - tier.offEachCents * tier.slotCount);
        } else {
          discounted = Math.round((slotsFull * (100 - tier.pct)) / 100);
        }
        this.checkedAddons().forEach((v) => {
          full += v.priceCents;
          discounted += Math.round((v.priceCents * (100 - v.pct)) / 100);
        });
        return { full: full, discounted: discounted, pct: tier.pct };
      }

      renderSummary() {
        /* The summary block is gone from the snippet — the subtotal lives in
           the buy button now, Ridge-style. If an older copy of the snippet is
           still in place, hide its summary outright rather than feeding it. */
        const legacy = this.querySelector('.tt-bundle-summary');
        if (legacy) legacy.hidden = true;
      }

      renderAtc() {
        const complete = this.isComplete();
        const t = this.totals();
        this.atcBtn.disabled = !complete || this.pending;
        this.atcBtn.setAttribute('aria-busy', this.pending ? 'true' : 'false');
        this.atcBtn.textContent = this.pending
          ? this.data.strings.addingLabel
          : t
            ? this.data.strings.atcLabel + ' ' + this.formatMoney(t.discounted)
            : this.data.strings.atcLabel;
        this.atcReason.hidden = complete;
        this.atcReason.textContent = this.data.strings.atcReason;
      }

      /* ---------- cart ---------- */

      buildItems() {
        const tier = this.tiers[this.state.tierIndex];
        const bundleId =
          window.crypto && window.crypto.randomUUID
            ? window.crypto.randomUUID()
            : 'bb-' + Date.now() + '-' + Math.floor(Math.random() * 1e9);
        const byVariant = {};
        this.state.slots.forEach((handle) => {
          const p = this.productsByHandle[handle];
          if (!byVariant[p.variantId]) byVariant[p.variantId] = { product: p, quantity: 0 };
          byVariant[p.variantId].quantity += 1;
        });
        const items = Object.keys(byVariant).map((variantId) => {
          const item = { id: parseInt(variantId, 10), quantity: byVariant[variantId].quantity };
          if (tier.slotCount > 1) {
            item.properties = {
              _bundle_id: bundleId,
              _bundle_tier: tier.key,
              _bundle_discount_pct: String(tier.pct)
            };
            if (this.data.strings.bundleProperty) {
              item.properties['Bundle'] = this.data.strings.bundleProperty;
            }
          }
          return item;
        });
        /* ticked add-ons are plain lines — no bundle properties, and merged
           into an existing line if the same variant is already in the pack */
        this.checkedAddons().forEach((v) => {
          const same = items.find((i) => i.id === v.id && !i.properties);
          if (same) same.quantity += 1;
          else items.push({ id: v.id, quantity: 1 });
        });
        return items;
      }

      addToCart() {
        if (this.pending || !this.isComplete()) return;
        this.pending = true;
        this.clearError();
        this.renderAtc();

        const items = this.buildItems();
        const base =
          window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add';
        const url = base.indexOf('.js') === -1 ? base + '.js' : base;

        /* Open the theme's own cart drawer after adding, exactly like the
           standard Add to cart does — via the drawer's public API only. If the
           drawer is missing or its API changed, fall back to the cart page. */
        const payload = { items: items };
        const drawer =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const drawerUsable =
          drawer &&
          typeof drawer.renderContents === 'function' &&
          typeof drawer.getSectionsToRender === 'function';
        if (drawerUsable) {
          try {
            payload.sections = drawer.getSectionsToRender().map((s) => s.id);
            payload.sections_url = window.location.pathname;
            if (typeof drawer.setActiveElement === 'function') {
              drawer.setActiveElement(document.activeElement);
            }
          } catch (e) {
            delete payload.sections;
            delete payload.sections_url;
          }
        }

        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload)
        })
          .then((response) =>
            response.json().then((body) => ({ ok: response.ok, body: body }))
          )
          .then((result) => {
            if (result.ok) {
              if (drawerUsable && payload.sections) {
                try {
                  this.pending = false;
                  this.renderAtc();
                  if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
                    window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                      source: 'product-form',
                      productVariantId: items[0] && items[0].id,
                      cartData: result.body
                    });
                  }
                  drawer.renderContents(result.body);
                  /* Belt and braces for the theme's empty-cart quirk: the
                     drawer's own renderContents clears is-empty from
                     .drawer__inner, but the CSS keys off cart-drawer.is-empty,
                     so a first add renders a blank panel. Harmless once the
                     theme's cart-drawer.js carries the same fix. */
                  drawer.classList.remove('is-empty');
                  const drawerFooter = document.getElementById('CartDrawer-Footer');
                  if (drawerFooter) drawerFooter.classList.remove('is-empty');
                  this.announce(this.data.strings.atcLabel);
                  /* the cart just grew, so the gap to the AOV target moved */
                  if (this.poolAddons && this.poolAddons.length && this.aovTarget > 0) {
                    this.refreshCartTotal();
                  }
                  return;
                } catch (e) {
                  /* drawer refused — fall through to the cart page */
                }
              }
              const cartUrl =
                window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';
              window.location.assign(cartUrl);
              return;
            }
            this.pending = false;
            this.showAddError(result.body);
          })
          .catch(() => {
            this.pending = false;
            this.showAddError(null);
          });
      }

      showAddError(body) {
        const description = body && (body.description || body.message) ? String(body.description || body.message) : '';
        let message = '';
        const failedTitles = [];
        this.state.slots.forEach((handle) => {
          const p = this.productsByHandle[handle];
          if (p && description.indexOf(p.title) !== -1 && failedTitles.indexOf(p.title) === -1) {
            failedTitles.push(p.title);
            this.failedHandles.add(handle);
            p.available = false;
          }
        });
        if (failedTitles.length) {
          message = failedTitles
            .map((t) => this.data.strings.errorItem.replace('[product]', t))
            .join(' ');
        } else {
          message = description || this.data.strings.errorGeneric;
        }
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
        this.announce(message);
        this.render();
      }

      clearError() {
        this.failedHandles.clear();
        if (this.errorEl) {
          this.errorEl.hidden = true;
          this.errorEl.textContent = '';
        }
      }

      /* ---------- money (driven by shop.money_format, cents in) ---------- */

      formatMoney(cents) {
        const format = this.data.moneyFormat || '${{amount}}';
        const units = cents / 100;
        const thousands = (s) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => {
          switch (key) {
            case 'amount_no_decimals':
              return thousands(String(Math.round(units)));
            case 'amount_with_comma_separator':
              return units.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            case 'amount_no_decimals_with_comma_separator':
              return String(Math.round(units)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            default:
              return thousands(units.toFixed(2));
          }
        });
      }

      announce(text) {
        this.liveRegion.textContent = '';
        this.liveRegion.textContent = text;
      }
    }
  );
}
